import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  Link,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import config from '../../config.js';

const FormContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  maxWidth: 600,
  margin: '20px auto',
  borderRadius: 16,
  boxShadow: 'none',
  display: 'flex',
  flexDirection: 'column',
  minHeight: '90vh',
  [theme.breakpoints.down('sm')]: {
    margin: '10px',
    padding: theme.spacing(2),
  },
}));

const Form = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  flex: 1,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    height: '56px',
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  height: 56,
  borderRadius: 50,
  fontSize: '1.1rem',
  textTransform: 'none',
  marginTop: 'auto',
  '&.Mui-disabled': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    opacity: 0.5,
  },
  [theme.breakpoints.down('sm')]: {
    height: 56,
  },
}));

function Login({ onToggleAuth }) {
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  
  const { login, setCurrentUser, api } = useAuth();
  const navigate = useNavigate();

  const startCountdown = () => {
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendVerificationCode = async () => {
    setError('');
    setLoading(true);
    
    try {
      if (!email.trim() || !email.includes('@')) {
        setError('Please enter a valid email address');
        return;
      }

      const response = await api.post('/api/auth/login', {
        email: email.trim().toLowerCase()
      });

      if (response.data.status === 'success') {
        setCodeSent(true);
        startCountdown();
        setActiveStep(1); // Move to verification step
      } else {
        setError(response.data.message || 'Failed to send verification code');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setError('');
    setLoading(true);
    
    try {
      if (!verificationCode.trim()) {
        setError('Please enter the verification code');
        return;
      }

      console.log('Verifying code...');
      const loginResponse = await login({
        email: email.trim().toLowerCase(),
        code: verificationCode.trim()
      });
      
      console.log('Login response received:', loginResponse);
      
      if (loginResponse.status === 'success') {
        console.log('Login successful, navigating to play...');
        // Navigate immediately since the auth state is already set in the login function
        navigate('/play', { replace: true });
      } else {
        console.log('Login failed:', loginResponse.message);
        setError(loginResponse.message || 'Invalid verification code');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.response?.data?.message || 'Failed to verify code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (codeSent) {
      await handleVerifyCode();
    } else {
      await handleSendVerificationCode();
    }
  };

  return (
    <Box sx={{ paddingTop: 4, px: 2 }}>   
      <FormContainer>
        <Typography variant="h3" gutterBottom sx={{ mb: 4, fontWeight: 600, color: 'black' }}>
          Welcome Back
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        
        <Form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <StyledTextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              disabled={codeSent}
            />
            
            {codeSent && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <StyledTextField
                  label="Verification Code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  required
                  fullWidth
                  autoFocus
                />
                {countdown > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Resend code in {countdown}s
                  </Typography>
                )}
                {countdown === 0 && (
                  <Button
                    onClick={handleSendVerificationCode}
                    disabled={loading}
                    sx={{ textTransform: 'none' }}
                  >
                    Resend Code
                  </Button>
                )}
              </Box>
            )}
          </Box>

          <StyledButton
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            fullWidth
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : codeSent ? (
              'Verify Code'
            ) : (
              'Proceed'
            )}
          </StyledButton>
        </Form>
        
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Don't have an account?{' '}
            <Link
              component="button"
              onClick={onToggleAuth}
              sx={{ 
                fontWeight: 500,
                textDecoration: 'none',
                color: 'primary.main',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              Sign up
            </Link>
          </Typography>
        </Box>
      </FormContainer>
    </Box>
  );
}

export default Login; 